import { generatePath } from 'react-router-dom';
import { flattenExpanded } from './obj';
import { beautify } from './str';
import { ROUTES } from '../consts/routes';
import { transform } from './transform';
import { parse } from 'mathjs';

export const buildUrl = (rootUrl, args = {}, options = {}) => {
    let tmp = { ...args };
    if (!rootUrl) return '';
    let { url, queryParams } = getQueryParams(rootUrl);
    let origin = '';
    try {
        origin = new URL(url).origin;
    } catch (error) {}

    if (origin && url.indexOf(origin) !== -1) {
        url = url.replace(origin, '');
    }

    if (url[url.length - 1] !== '/') {
        url += '/';
    }
    tmp = flattenExpanded(tmp);
    if (tmp) {
        if (!options.ignoreExtra) {
            Object.keys(tmp).forEach((key) => {
                if (url.indexOf(':' + key) === -1 && getValue(tmp, key)) {
                    queryParams[key] = getValue(tmp, key);
                }
            });
        }
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value[0] === ':') {
                queryParams[key] = getValue(tmp, value.replaceAll(':', ''));
            }
        });
        url = parseUrl(url, tmp);
        if (Object.keys(queryParams).length) {
            url += '?' + new URLSearchParams(queryParams).toString();
        }
    }
    return origin + url;
};

export const getValue = (data, key = '', join = true, defaultValue = '', delimiter = ', ') => {
    const tmp = { ...data };
    try {
        if (!key) {
            return tmp;
        }
        if (Array.isArray(key)) {
            const values = [];
            key.forEach((row) => {
                values.push(
                    row.split('.').reduce((o, i) => {
                        return o[i];
                    }, tmp)
                );
            });
            return (
                (join ? values.filter((n) => n).join(delimiter) : values.filter((n) => n)) ||
                defaultValue
            );
        } else {
            return key.split('.').reduce((o, i) => o[i], tmp) || defaultValue;
        }
    } catch (error) {
        return defaultValue;
    }
};

export const getOptionValues = (data) => {
    return data.map((item) => item.value);
};

export const getInitial = (str) => {
    return str?.[0] ? str[0].toUpperCase() : '~';
};

export const parseUrl = (url, params, queryParams) => {
    if (url) {
        const tmp = [];
        url.split('/').forEach((x) => {
            if (x.indexOf(':') !== -1 && x.indexOf('.') !== -1) {
                const k = x.replaceAll('.', '_').replaceAll(':', '');
                params[k] = getValue(params, x.replaceAll(':', ''));
                x = x.replaceAll('.', '_');
            }
            tmp.push(x);
        });
        url = tmp.join('/');
    }

    try {
        if (!url || !params) {
            return '';
        } else if (url.indexOf('~') !== -1) {
            return url.replaceAll('~', params);
        }
        url = generatePath(url, params);
        if (queryParams && Object?.keys(queryParams)?.length > 0) {
            const tmpQueryParams = {};
            Object.entries({ ...queryParams }).forEach(([key, value]) => {
                if (value) {
                    tmpQueryParams[key] = getValue(params, value);
                }
            });
            url = buildUrl(url, tmpQueryParams);
        }
        return url;
    } catch (error) {
        return url;
    }
};

export const getQueryParams = (url) => {
    const queryParams = {};
    const queryString = url.split('?')[1];
    let baseUrl = url;

    if (queryString) {
        const paramPairs = queryString.split('&');
        paramPairs.forEach((pair) => {
            const [key, value] = pair.split('=');
            queryParams[key] = decodeURIComponent(value);
        });
        baseUrl = url.split('?')[0];
    }

    return { url: baseUrl, queryParams };
};

export const slugify = (str, delimiter = '_') => {
    if (!str) return '';
    return str
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, delimiter)
        .replace(/^-+|-+$/g, '');
};

export const _delete = (obj, props) => {
    if (typeof props === 'string') {
        props = [props];
    }
    props.forEach((prop) => {
        if (obj[prop]) {
            delete obj[prop];
        }
    });
    return obj;
};

export const fixId = (data) => {
    if (Array.isArray(data)) {
        data.forEach((row, index) => {
            data[index] = fixId(row);
        });
    } else {
        if (data) {
            Object.entries(data).forEach(([key, value]) => {
                if (key === '_id') {
                    data['id'] = data['_id'];
                }
                if (value && typeof value === 'object') {
                    data[key] = fixId(value);
                }
            });
        }
    }
    return data;
};

export const packDotNotation = (arr) => {
    const result = [];

    arr.forEach((str) => {
        const parts = str.split('.');
        let current = result;

        parts.forEach((part, index) => {
            const existing = current.find((item) => item.name === part);

            if (existing) {
                current = existing.children;
            } else {
                const newItem = { name: part, children: [] };
                current.push(newItem);
                current = newItem.children;
            }
        });
    });
    return result;
};

export const breadcrumbs = (data, map) => {
    const pathnames = data.split('/').filter((crumb) => crumb);
    const list = ['admin', 'identity', 'crm', 'dashboard', 'los', 'disbursal', 'unr', 'lenders'];
    const breadcrumbList = pathnames
        .map((path, index) => {
            return {
                title: map && map[path] ? map[path] : beautify(path),
                path: `/${pathnames.slice(0, index + 1).join('/')}`,
                isSeparator: list.includes(path),
                isActive: false,
            };
        })
        .filter((crumb) => !crumb.isSeparator);
    breadcrumbList.unshift({
        title: 'Home',
        path: ROUTES.dashboard.url,
        isSeparator: false,
        isActive: false,
    });
    return breadcrumbList;
};

export const perc = (value, total) => {
    if (!value || isNaN(value) || !total) {
        return 0;
    }
    return !total ? '∞' : ((value / total) * 100).toFixed(2) * 1;
};

export const amountWords = (num, symbol = true) => {
    let val = 0;
    let mutliplier = 1;
    if (num < 0) {
        mutliplier = -1;
        num *= -1;
    }
    if (num > 999 && num < 100000) {
        val = mutliplier * parseFloat((num / 1000).toFixed(2)) + 'K';
    } else if (num >= 10000000) {
        val = mutliplier * parseFloat((num / 10000000).toFixed(2)) + ' Cr';
    } else if (num >= 100000) {
        val = mutliplier * parseFloat((num / 100000).toFixed(2)) + ' L';
    } else if (num <= 999) {
        val = mutliplier * num;
    }
    return symbol ? '₹' + val : val;
};

export const PMT = (ir, np, pv, fv, type) => {
    /*
     * ir   - interest rate per month
     * np   - number of periods (months)
     * pv   - present value
     * fv   - future value
     * type - when the payments are due:
     *        0: end of the period, e.g. end of month (default)
     *        1: beginning of period
     */
    var pmt, pvif;

    fv || (fv = 0);
    type || (type = 0);

    if (ir === 0) return -(pv + fv) / np;

    pvif = Math.pow(1 + ir, np);
    pmt = (-ir * (pv * pvif + fv)) / (pvif - 1);

    if (type === 1) pmt /= 1 + ir;

    return pmt;
};

// reverse -> EMI Amount (Monthly),  Interest Rate, Tenure -> max Loan amount

export const percClass = (value) => {
    if (isNaN(value)) return 'secondary';
    if (value > 80) return 'success';
    if (value > 40) return 'warning';
    return 'danger';
};

export const percColor = (value) => {
    if (value > 80) return '#5ac360';
    if (value > 40) return '#ecc33c';
    return '#e4445c';
};

export const statusBadge = (status, padding = 'py-3 px-4', light = true) => {
    return (
        <span
            className={`badge ${padding} badge${light ? '-light' : ''}-${transform(
                'class',
                status
            )} d-inline`}>
            {beautify(status)}
        </span>
    );
};

export const validateExpression = (expression, data) => {
    function resolvePath(obj, path) {
        return path
            .split('.')
            .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
    }

    function evaluateRule(rule, data) {
        const fieldValue = resolvePath(data, rule.field);
        const value = rule.value;

        switch (rule.operator) {
            case '=':
                return fieldValue === value;
            case '!=':
                return fieldValue !== value && fieldValue !== undefined && fieldValue !== null;
            case '>':
                return parseFloat(fieldValue) > parseFloat(value);
            case '>=':
                return parseFloat(fieldValue) >= parseFloat(value);
            case '<':
                return parseFloat(fieldValue) < parseFloat(value);
            case '<=':
                return parseFloat(fieldValue) <= parseFloat(value);
            case 'contains':
                return typeof fieldValue === 'string' && fieldValue.includes(value);
            case 'does not contain':
                return typeof fieldValue === 'string' && !fieldValue.includes(value);
            case 'beginsWith':
                return typeof fieldValue === 'string' && fieldValue.startsWith(value);
            case 'does not begin with':
                return typeof fieldValue === 'string' && !fieldValue.startsWith(value);
            case 'endsWith':
                return typeof fieldValue === 'string' && fieldValue.endsWith(value);
            case 'does not end with':
                return typeof fieldValue === 'string' && !fieldValue.endsWith(value);
            case 'is null':
                return fieldValue === null || fieldValue === undefined;
            case 'is not null':
                return fieldValue !== null && fieldValue !== undefined;
            case 'in':
                return value
                    .split(',')
                    .map((v) => v.trim())
                    .includes(fieldValue);
            case 'not in':
                return !value
                    .split(',')
                    .map((v) => v.trim())
                    .includes(fieldValue);
            case 'between':
                if (!Array.isArray(value) || value.length !== 2) {
                    throw new Error(`"between" operator requires an array with exactly 2 values.`);
                }
                return (
                    parseFloat(fieldValue) >= parseFloat(value[0]) &&
                    parseFloat(fieldValue) <= parseFloat(value[1])
                );
            case 'not between':
                if (!Array.isArray(value) || value.length !== 2) {
                    throw new Error(
                        `"not between" operator requires an array with exactly 2 values.`
                    );
                }
                return (
                    parseFloat(fieldValue) < parseFloat(value[0]) ||
                    parseFloat(fieldValue) > parseFloat(value[1])
                );
            default:
                throw new Error(`Unsupported operator: ${rule.operator}`);
        }
    }

    function evaluateExpression(expr, data) {
        const { combinator, not, rules } = expr;

        const results = rules.map((rule) => {
            if (rule.rules) {
                // Nested expression
                return evaluateExpression(rule, data);
            }
            // Individual rule
            return evaluateRule(rule, data);
        });

        let result;
        if (!combinator || combinator === 'and') {
            result = results.every(Boolean);
        } else if (combinator === 'or') {
            result = results.some(Boolean);
        } else {
            throw new Error(`Unsupported combinator: ${combinator}`);
        }

        return not ? !result : result;
    }

    return evaluateExpression(expression, data);
};

export const getCurl = (request) => {
    const { url, method, headers, body } = request;

    let curlCommand = `curl -X ${method?.toUpperCase() || 'POST'} "${url}"`;

    if (headers) {
        Object.entries(headers).forEach(([key, value]) => {
            curlCommand += ` \\\n     -H "${key}: ${value}"`;
        });
    }

    if (body) {
        curlCommand += ` \\\n     -d '${JSON.stringify(body)}'`;
    }

    return curlCommand;
};

export const generateReadableText = (node, descriptions = {}) => {
    // Handle variable nodes (e.g., STORE)
    if (node.isSymbolNode) {
        return descriptions[node.name] || node.name;
    }

    // Handle property access (e.g., STORE.bre.score.status)
    if (node.isAccessorNode) {
        const getFullPath = (n) => {
            if (n.isSymbolNode) return n.name;
            if (n.isAccessorNode)
                return `${getFullPath(n.object)}${n.index.name ? `.${n.index.name}` : n.index}`;
            return '';
        };
        const fullPath = getFullPath(node);
        return descriptions[fullPath] || fullPath;
    }

    // Handle constants (e.g., false, 300000)
    if (node.isConstantNode) {
        return node.value.toString();
    }

    // Handle operators (e.g., ==, >, and, or)
    if (node.isOperatorNode) {
        const left = generateReadableText(node.args[0], descriptions);
        const right = node.args[1] ? generateReadableText(node.args[1], descriptions) : '';

        console.log(left, right, node);

        switch (node.op) {
            case 'not':
                return `not ${left}`;
            case '==':
                return `${left} is equal to ${right}`;
            case '!=':
                return `${left} is not equal to ${right}`;
            case '>':
                return `${left} exceeds ${right}`; // More natural than "is greater than"
            case 'and':
                return `${left} and ${right}`;
            case 'or':
                return `${left} or ${right}`;
            default:
                return `${left} ${node.op} ${right}`; // Fallback for unhandled operators
        }
    }

    if (node.isParenthesisNode) {
        return `(${generateReadableText(node.content, descriptions)})`;
    }

    return '';
};

export const parseMakoToReadable = (makoString, descriptions = {}) => {
    const expression = extractExpression(makoString);
    const ast = parse(expression);
    let str = generateReadableText(ast, descriptions);
    if (str.indexOf('STORE.') === 0) {
        str = str.replaceAll('STORE.', '');
    }
    if (str.indexOf('data.') === 0) {
        str = str.replaceAll('data.', '');
    }
    return str;
};

export const extractExpression = (makoString) => {
    const expression = makoString
        .replace(/\$\{(.*)\}\.safe_eval/, '$1')
        .replace(/False/g, 'false')
        .replace(/True/g, 'true');
    return expression;
};
